import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import InstructorAnalyticsService from '../../services/instructorAnalyticsService';
import { 
  TrendingUp, 
  Users, 
  BookOpen, 
  DollarSign,
  Eye,
  Clock,
  Star,
  Download,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Award,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

interface AnalyticsData {
  overview: {
    totalRevenue: number;
    totalStudents: number;
    totalCourses: number;
    averageRating: number;
    revenueGrowth: number;
    studentsGrowth: number;
    coursesGrowth: number;
    ratingGrowth: number;
  };
  coursePerformance: CourseAnalytics[];
  studentEngagement: {
    activeStudents: number;
    averageTimeSpent: number;
    completionRate: number;
    retentionRate: number;
  };
  revenueAnalytics: {
    monthlyRevenue: MonthlyRevenue[];
    topSellingCourses: TopCourse[];
  };
  reviews: {
    totalReviews: number;
    averageRating: number;
    ratingDistribution: RatingDistribution[];
    recentReviews: Review[];
  };
}

interface CourseAnalytics {
  id: string;
  title: string;
  enrollments: number;
  completions: number;
  revenue: number;
  averageRating: number;
  averageTimeSpent: number;
  completionRate: number;
  dropoffRate: number;
}

interface MonthlyRevenue {
  month: string;
  revenue: number;
  enrollments: number;
}

interface TopCourse {
  id: string;
  title: string;
  revenue: number;
  enrollments: number;
}

interface RatingDistribution {
  stars: number;
  count: number;
  percentage: number;
}

interface Review {
  id: string;
  studentName: string;
  courseName: string;
  rating: number;
  comment: string;
  date: string;
}

const InstructorAnalyticsPage: React.FC = () => {
  const { currentTenant } = useTenantContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { instructor, hasPermission, isLoading: authLoading } = useInstructorPermissions(['analytics_view']);
  
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<string>('30d');

  useEffect(() => {
    if (currentTenant) {
      loadAnalytics();
    }
  }, [currentTenant, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      if (!currentTenant?.id || !instructor?.id) {
        setLoading(false);
        return;
      }

      // Charger les vraies données analytics
      const [overview, courseAnalytics, studentEngagement, revenueAnalytics, reviewAnalytics] = await Promise.all([
        InstructorAnalyticsService.getAnalyticsOverview(currentTenant.id, instructor.id, timeRange),
        InstructorAnalyticsService.getCourseAnalytics(currentTenant.id, instructor.id, timeRange),
        InstructorAnalyticsService.getStudentEngagement(currentTenant.id, instructor.id, timeRange),
        InstructorAnalyticsService.getRevenueAnalytics(currentTenant.id, instructor.id, timeRange),
        InstructorAnalyticsService.getReviewAnalytics(currentTenant.id, instructor.id, 10)
      ]);
      
      setAnalytics({
        overview,
        coursePerformance: courseAnalytics,
        studentEngagement,
        revenueAnalytics,
        reviews: reviewAnalytics
      });
    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getGrowthColor = (growth: number) => {
    return growth >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getGrowthIcon = (growth: number) => {
    return growth >= 0 ? '↗' : '↘';
  };

  if (loading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  if (!analytics) {
    return (
      <InstructorLayout>
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Données d'analytics non disponibles
          </h3>
          <p className="text-gray-600">
            Les données d'analytics apparaîtront ici une fois que vous aurez des étudiants inscrits.
          </p>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">
              Analysez les performances de vos cours et l'engagement de vos étudiants
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 derniers jours</SelectItem>
                <SelectItem value="30d">30 derniers jours</SelectItem>
                <SelectItem value="90d">90 derniers jours</SelectItem>
                <SelectItem value="1y">Cette année</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Vue d'ensemble */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenus totaux</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(analytics.overview.totalRevenue)}</div>
              <p className={`text-xs ${getGrowthColor(analytics.overview.revenueGrowth)}`}>
                {getGrowthIcon(analytics.overview.revenueGrowth)} {Math.abs(analytics.overview.revenueGrowth)}% vs période précédente
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Étudiants totaux</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.totalStudents}</div>
              <p className={`text-xs ${getGrowthColor(analytics.overview.studentsGrowth)}`}>
                {getGrowthIcon(analytics.overview.studentsGrowth)} {Math.abs(analytics.overview.studentsGrowth)}% vs période précédente
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cours actifs</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.totalCourses}</div>
              <p className={`text-xs ${getGrowthColor(analytics.overview.coursesGrowth)}`}>
                {getGrowthIcon(analytics.overview.coursesGrowth)} {Math.abs(analytics.overview.coursesGrowth)}% vs période précédente
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Note moyenne</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.averageRating}/5</div>
              <p className={`text-xs ${getGrowthColor(analytics.overview.ratingGrowth)}`}>
                {getGrowthIcon(analytics.overview.ratingGrowth)} {Math.abs(analytics.overview.ratingGrowth)} vs période précédente
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance des cours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Performance des cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics.coursePerformance.map((course) => (
                  <div key={course.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium">{course.title}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span>{course.enrollments} inscrits</span>
                          <span>{course.completions} terminés</span>
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current text-yellow-500" />
                            {course.averageRating}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(course.revenue)}</p>
                        <p className="text-sm text-gray-600">{course.completionRate.toFixed(1)}% terminé</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Taux de complétion</span>
                        <span>{course.completionRate.toFixed(1)}%</span>
                      </div>
                      <Progress value={course.completionRate} className="h-2" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Engagement des étudiants */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Engagement des étudiants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{analytics.studentEngagement.activeStudents}</div>
                    <div className="text-sm text-gray-600">Étudiants actifs</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {formatTime(analytics.studentEngagement.averageTimeSpent)}
                    </div>
                    <div className="text-sm text-gray-600">Temps moyen</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span>Taux de complétion</span>
                      <span className="font-semibold">{analytics.studentEngagement.completionRate}%</span>
                    </div>
                    <Progress value={analytics.studentEngagement.completionRate} className="h-2" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span>Taux de rétention</span>
                      <span className="font-semibold">{analytics.studentEngagement.retentionRate}%</span>
                    </div>
                    <Progress value={analytics.studentEngagement.retentionRate} className="h-2" />
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Cours les plus populaires</h4>
                  <div className="space-y-2">
                    {analytics.revenueAnalytics.topSellingCourses.slice(0, 3).map((course, index) => (
                      <div key={course.id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="w-6 h-6 p-0 flex items-center justify-center text-xs">
                            {index + 1}
                          </Badge>
                          <span className="truncate">{course.title}</span>
                        </div>
                        <span className="font-medium">{course.enrollments}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Évolution des revenus */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Évolution des revenus
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics.revenueAnalytics.monthlyRevenue.map((month) => (
                  <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{month.month}</p>
                      <p className="text-sm text-gray-600">{month.enrollments} nouvelles inscriptions</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{formatCurrency(month.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Avis et notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Avis et évaluations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold">{analytics.reviews.averageRating}</div>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(analytics.reviews.averageRating)
                            ? 'fill-current text-yellow-500'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Basé sur {analytics.reviews.totalReviews} avis
                  </p>
                </div>

                <div className="space-y-2">
                  {analytics.reviews.ratingDistribution.map((rating) => (
                    <div key={rating.stars} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-12">
                        <span className="text-sm">{rating.stars}</span>
                        <Star className="h-3 w-3 fill-current text-yellow-500" />
                      </div>
                      <div className="flex-1">
                        <Progress value={rating.percentage} className="h-2" />
                      </div>
                      <span className="text-sm text-gray-600 w-12">{rating.count}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Avis récents</h4>
                  <div className="space-y-3">
                    {analytics.reviews.recentReviews.map((review) => (
                      <div key={review.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-sm">{review.studentName}</p>
                            <p className="text-xs text-gray-600">{review.courseName}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-3 w-3 ${
                                  star <= review.rating
                                    ? 'fill-current text-yellow-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{review.comment}</p>
                        <p className="text-xs text-gray-500 mt-2">{formatDate(review.date)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions rapides */}
        <Card>
          <CardHeader>
            <CardTitle>Actions recommandées</CardTitle>
            <CardDescription>
              Suggestions pour améliorer vos performances
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                <Target className="h-6 w-6 text-blue-600 mb-2" />
                <h4 className="font-medium text-blue-900 mb-1">Améliorer l'engagement</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Le taux de complétion de "TypeScript Débutants" est faible (34.3%)
                </p>
                <Button size="sm" variant="outline" className="border-blue-300">
                  Analyser le cours
                </Button>
              </div>

              <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                <MessageSquare className="h-6 w-6 text-green-600 mb-2" />
                <h4 className="font-medium text-green-900 mb-1">Répondre aux avis</h4>
                <p className="text-sm text-green-700 mb-3">
                  2 nouveaux avis en attente de réponse
                </p>
                <Button size="sm" variant="outline" className="border-green-300">
                  Voir les avis
                </Button>
              </div>

              <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
                <Award className="h-6 w-6 text-purple-600 mb-2" />
                <h4 className="font-medium text-purple-900 mb-1">Créer du contenu</h4>
                <p className="text-sm text-purple-700 mb-3">
                  Vos cours JavaScript sont très populaires
                </p>
                <Button size="sm" variant="outline" className="border-purple-300">
                  Nouveau cours
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </InstructorLayout>
  );
};

export default InstructorAnalyticsPage;
